import { motion } from "framer-motion";

// Masked line-by-line reveal for hero
export const MaskedLines = ({ lines, className = "", delay = 0.2 }) => (
  <span className={className}>
    {lines.map((line, i) => (
      <span key={i} className="line-mask pb-[0.14em]">
        <motion.span
          className="block pb-[0.06em]"
          initial={{ y: "115%" }}
          animate={{ y: "0%" }}
          transition={{ duration: 1.1, delay: delay + i * 0.14, ease: [0.76, 0, 0.24, 1] }}
        >
          {line}
        </motion.span>
      </span>
    ))}
  </span>
);

// Cinematic letter-by-letter reveal — used for hero headline entrance.
// Accepts either a plain string OR an array of { text, className } segments for coloured runs.
// Preserves word boundaries so line breaks never split a word in half.
export const LetterReveal = ({ text, segments, className = "", stagger = 0.04, delay = 0.25 }) => {
  const runs = segments || (text != null ? [{ text }] : []);
  let idx = 0;
  const renderChar = (ch) => {
    const i = idx++;
    return (
      <motion.span
        key={i}
        className="inline-block"
        initial={{ opacity: 0, y: "0.35em" }}
        animate={{ opacity: 1, y: 0 }}
        transition={{
          duration: 0.9,
          delay: delay + i * stagger,
          ease: [0.22, 1, 0.36, 1],
        }}
      >
        {ch}
      </motion.span>
    );
  };
  return (
    <span className={className} aria-label={text || runs.map((r) => r.text).join("")}>
      {runs.map((run, ri) => {
        // Split on spaces but keep them as separate " " tokens so we can render them as gaps
        const tokens = String(run.text || "").split(/(\s+)/);
        return (
          <span key={ri} className={run.className || ""}>
            {tokens.map((tok, ti) => {
              if (tok === "") return null;
              if (/^\s+$/.test(tok)) {
                idx++; // consume delay slot for the space
                return <span key={`s-${ri}-${ti}`}>&nbsp;</span>;
              }
              // Wrap each word so it never breaks in the middle
              return (
                <span key={`w-${ri}-${ti}`} className="inline-block whitespace-nowrap">
                  {tok.split("").map((ch) => renderChar(ch))}
                </span>
              );
            })}
          </span>
        );
      })}
    </span>
  );
};

// Scroll-triggered fade/rise
export const Reveal = ({ children, delay = 0, y = 40, className = "", as: Tag = motion.div }) => (
  <Tag
    className={className}
    initial={{ opacity: 0, y }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-80px" }}
    transition={{ duration: 0.9, delay, ease: [0.22, 1, 0.36, 1] }}
  >
    {children}
  </Tag>
);

export const StaggerGroup = ({ children, className = "", ...rest }) => (
  <motion.div
    className={className}
    initial="hidden"
    whileInView="show"
    viewport={{ once: true, margin: "-60px" }}
    variants={{ hidden: {}, show: { transition: { staggerChildren: 0.12 } } }}
    {...rest}
  >
    {children}
  </motion.div>
);

export const StaggerItem = ({ children, className = "", ...rest }) => (
  <motion.div
    className={className}
    variants={{ hidden: { opacity: 0, y: 40 }, show: { opacity: 1, y: 0 } }}
    transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
    {...rest}
  >
    {children}
  </motion.div>
);
